import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import * as yup from "yup";
import { ZipCodeFetch } from "@/api/address";
import { useFormStore } from "@/store/form";

import Column from "@/components/Column";
import Input from "@/components/Input";
import { sendUserJourney } from "@/api/userJourney";
import { UserJourneyRawData } from "@/api/userJourney/types";

import AddressCard from "@/components/AddressCard";
import { useActiveStore } from "@/store/activePages";
import { useBlockRequestStore } from "@/store/blockRequest";
import LoadingAddress from "@/components/LoadingAddress";
import { useNavigate } from "react-router-dom";
import { validateZipCode } from "@/utils/validators/zipCode";
import Select from "@/components/Select";
import SelectItem from "@/components/Select/Item";
import { useUFs } from "@/api/uf";
import { useCities } from "@/api/city";
import Button from "@/components/Button/Button";
import api from "@/api";
import { ViaCepItem } from "@/app/address-info/unknown/AddressInfoUnknown";
import Columns from "@/components/Columns";
import { formatZipCode } from "@/utils";
import Text from "@/components/Text";

const keysToSerialize = {
  "address.street": "streetName",
  "address.neighborhood": "locality",
  "address.stateCode": "stateOrProvince",
  "address.cityName": "city",
} as const;

const keysToManuallySerialize = {
  "address.street": "street",
  "address.neighborhood": "neighborhood",
} as const;

const addressFields = [
  { key: "street", dataKey: "streetName" },
  { key: "neighborhood", dataKey: "locality" },
  { key: "stateCode", dataKey: "stateOrProvince" },
  { key: "cityName", dataKey: "city" },
];

let previousZipCodeValue: string | null = null;

export const addressInfoSchema = yup.object().shape({
  address: yup.object().shape({
    zipCode: yup
      .string()
      .test("zipCode", "CEP inválido", function (value) {
        if (value && value !== previousZipCodeValue) {
          previousZipCodeValue = value;

          return validateZipCode(value);
        }

        return true;
      })
      .required("O CEP é obrigatório"),
    street: yup.string().trim().required("A rua é obrigatória"),
    neighborhood: yup.string().trim().required("O bairro é obrigatório"),
    noNumber: yup.boolean(),
    number: yup.string().when("noNumber", {
      is: true,
      then: () => yup.string().notRequired(),
      otherwise: () =>
        yup
          .string()
          .trim()
          .required("O campo é obrigatório")
          .max(50, "Máximo 50 caracteres"),
    }),
    complement: yup.string().trim().max(50, "Máximo 50 caracteres"),
    referencePoint: yup.string().when("noNumber", {
      is: true,
      then: () =>
        yup
          .string()
          .trim()
          .required("O campo é obrigatório quando Sem Número")
          .max(100, "Máximo 100 caracteres"),
      otherwise: () => yup.string().trim().max(100, "Máximo 100 caracteres"),
    }),
    stateCode: yup.string(),
    cityName: yup.string(),
  }),
});

interface AddresInfoFildsProps {
  setFormIsValid(isValid: boolean): void;
  setShowContinueButton(showContinueButton: boolean): void;
}

interface Setters {
  "address.street": React.Dispatch<React.SetStateAction<string>>;
  "address.neighborhood": React.Dispatch<React.SetStateAction<string>>;
  "address.stateCode": React.Dispatch<React.SetStateAction<string>>;
  "address.cityName": React.Dispatch<React.SetStateAction<string>>;
  [key: string]: React.Dispatch<React.SetStateAction<string>>;
}

const AddressInfoFields: React.FC<AddresInfoFildsProps> = ({
  setFormIsValid,
  setShowContinueButton,
}) => {
  const navigate = useNavigate();
  const form = useFormStore((store) => store.value);
  const address = useFormStore((store) => store.value.address);
  const uuid = useFormStore((store) => store.value.uuid);
  const addPage = useActiveStore((store) => store.addPage);
  const saleId = useFormStore((store) => store.saleId);
  const {
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useFormContext();

  const [zipCode, setZipCode] = useState(address.zipCode);
  const [street, setStreet] = useState(address.street);
  const [neighborhood, setNeighborhood] = useState(address.neighborhood);
  const [number, setNumber] = useState(address.number);
  const [complement, setComplement] = useState(address.complement);
  const [referencePoint, setReferencePoint] = useState(address.referencePoint);
  const [noNumber, setNoNumber] = useState(address.noNumber);
  const [stateCode, setStateCode] = useState(address.stateCode);
  const [cityName, setCityName] = useState(address.cityName);
  const [cepGeneric, setCepGeneric] = useState(false);
  const [, setAllAttributesIsNull] = useState(true);
  const [errorMessage, setErrorMessage] = useState(false);

  const [uf, setUf] = useState(stateCode || "AC");
  const [city, setCity] = useState(cityName);

  const { ufs } = useUFs();
  const { cities } = useCities(uf);

  const keysToSetters: Setters = useMemo(() => {
    return {
      "address.street": setStreet,
      "address.neighborhood": setNeighborhood,
      "address.stateCode": setStateCode,
      "address.cityName": setCityName,
    };
  }, []);

  const blockRequest = useBlockRequestStore((store) => store.blockRequest);
  const setBlockRequest = useBlockRequestStore(
    (store) => store.setBlockRequest
  );
  const { data, isLoading } = ZipCodeFetch(zipCode, blockRequest);
  useEffect(() => {
    const infoForm = {
      address: {
        zipCode,
        stateCode,
        cityName,
        street,
        neighborhood,
        number,
        complement,
        referencePoint,
        noNumber,
      },
    };

    setShowContinueButton(true);

    addressInfoSchema
      .isValid(infoForm)
      .then((isValid) => {
        setFormIsValid(isValid);
      })
      .catch(() => {
        setFormIsValid(false);
      });
  }, [
    zipCode,
    street,
    neighborhood,
    number,
    stateCode,
    cityName,
    complement,
    referencePoint,
    noNumber,
    setFormIsValid,
    setShowContinueButton,
  ]);

  const handleBlurUserJourney = useCallback(
    (name: string, value: unknown) => {
      // @ts-expect-error - TS doesn't know that errors is defined
      if (!(errors?.address && errors?.address[name])) {
        const addressValues = {
          [name]: value,
          ...{ ...getValues().address },
        };
        sendUserJourney({
          ...form,
          address: addressValues,
          ...{
            passo: "ENDERECO",
            uuid,
          },
        } as UserJourneyRawData);
      }
    },
    [errors?.address, getValues, uuid, form]
  );

  useEffect(() => {
    const hasData = Object.values(data || {}).some(
      (value) => value !== null && value !== ""
    );

    if (data?.erro && !blockRequest) {
      setError("address.zipCode", {
        message: "CEP inválido",
        type: "invalid",
      });
    } else if (hasData && data && !blockRequest) {
      let someData = false;
      addressFields.forEach(({ key, dataKey }) => {
        const dataValue =
          (data?.[dataKey as keyof typeof data] as string) ||
          getValues().address[key];
        setValue(`address.${key}`, dataValue);
        keysToSetters[`address.${key}`](dataValue);
      });

      Object.entries(keysToSerialize).forEach(([key, apiKey]) => {
        someData = someData || !!data[apiKey];
        clearErrors(key);
      });
      setCepGeneric(
        data?.addressType?.includes("Generico") &&
          !data?.streetName &&
          !data?.locality
      );
      setAllAttributesIsNull(!someData);
      sendUserJourney({
        ...form,
        address: { ...getValues().address },
        ...{
          passo: "ENDERECO",
          uuid,
        },
      } as UserJourneyRawData);
    } else if (!hasData && !blockRequest && getValues().address) {
      let someData = false;
      Object.entries(keysToManuallySerialize).forEach(([key, apiKey]) => {
        setValue(key, getValues().address[apiKey]);
        if (keysToSetters[key]) {
          keysToSetters[key](getValues().address[apiKey]);
        }
        someData = someData || !!getValues().address[apiKey];
        clearErrors(key);
      });

      setAllAttributesIsNull(!someData);
      const cleanZipCode = zipCode.replace(/\D/g, "");

      if (cleanZipCode && cleanZipCode.length === 8 && !isLoading) {
        setErrorMessage(true);
      } else {
        setErrorMessage(false);
      }
    } else if (blockRequest) {
      setValue("address.street", form.address.street);
      setValue("address.neighborhood", form.address.neighborhood);
      setValue("address.stateCode", form.address.stateCode);
      setValue("address.cityName", form.address.cityName);
      clearErrors("address.street");
      clearErrors("address.neighborhood");
      clearErrors("address.stateCode");
      clearErrors("address.cityName");
    }
  }, [
    blockRequest,
    clearErrors,
    data,
    form,
    getValues,
    isLoading,
    keysToSetters,
    setError,
    setValue,
    uuid,
    zipCode,
  ]);

  const showStreetAndNeighborhoodInputs = useCallback(() => {
    return data && (cepGeneric || data?.erro) && !blockRequest;
  }, [cepGeneric, data, blockRequest]);

  const showCityAndStateInputs = useCallback(() => {
    return !!(
      data &&
      ((cepGeneric && !data?.city && !data?.stateOrProvince) || data?.erro)
    );
  }, [cepGeneric, data]);

  // const [formIsValid, setFormIsValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState<ViaCepItem[] | null>(null);
  const [, setShowMsgSelectAddresses] = useState(false);

  const handleFindAddress = () => {
    const params = {
      uf,
      city,
      street,
    };
    // setLoading(true);
    api
      .get("/integrations/enderecos", { params })
      .then(({ data }) => {
        if (data.length) {
          setShowMsgSelectAddresses(true);
        }
        setAddresses(data);
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Column size={12} className="pb-4">
        <Input
          type="tel"
          mask="99999-999"
          name="address.zipCode"
          label="CEP"
          placeholder="99999-999"
          disabled={!!saleId}
          loading={isLoading}
          helperTextWithAction={() => (
            <div className="mt-1 w-full text-right text-tim-primary">
              <button
                type="button"
                disabled={!!saleId}
                onClick={() => {
                  addPage("unknown");
                  navigate("/address-info/unknown");
                }}
              >
                <span className="underline"> Não sei meu CEP</span>
              </button>
            </div>
          )}
          onChange={(e) => setZipCode(e.target.value)}
          onBlur={(e) => {
            setZipCode(e.target.value);
            handleBlurUserJourney("zipCode", e.target.value);
            setBlockRequest(false);
          }}
          value={zipCode}
          error={
            errorMessage
              ? "CEP inválido"
              : showCityAndStateInputs()
              ? "CEP não encontrtado"
              : ""
          }
        />
      </Column>
      <Column
        size={12}
        className={`pb-6 ${!cepGeneric && street ? "" : "hidden"}`}
      >
        <AddressCard
          cep={zipCode}
          street={street}
          neighborhood={neighborhood}
          state={stateCode}
        />
      </Column>

      <Column size={12} className="hidden" aria-hidden="true">
        <Input
          name="address.stateCode"
          label="Insira a sigla do estado"
          placeholder="Insira a sigla do estado"
          value={stateCode}
          disabled={!!saleId}
          onChange={(e) => setStateCode(e.target.value)}
          onBlur={(e) => {
            setStateCode(e.target.value);
            handleBlurUserJourney("stateCode", e.target.value);
          }}
        />
      </Column>

      <Column size={12} className="hidden" aria-hidden="true">
        <Input
          name="address.cityName"
          label="Insira o nome da cidade"
          placeholder="Insira o nome da cidade"
          value={cityName}
          disabled={!!saleId}
          onChange={(e) => setCityName(e.target.value)}
          onBlur={(e) => {
            setCityName(e.target.value);
            handleBlurUserJourney("cityName", e.target.value);
          }}
        />
      </Column>

      <Column
        size={12}
        className={`pb-6 ${showCityAndStateInputs() ? "" : "hidden"}`}
      >
        <Select
          name="uf"
          placeholder="Estado"
          value={uf}
          onChange={(e) => {
            setUf(e.target.value);
            setStateCode(e.target.value);
            handleBlurUserJourney("stateCode", e.target.value);
          }}
        >
          {ufs?.map((item) => (
            <SelectItem key={item.id} value={item.sigla}>
              {item.nome} - {item.sigla}
            </SelectItem>
          ))}
        </Select>
      </Column>
      <Column
        size={12}
        className={`pb-6 ${showCityAndStateInputs() ? "" : "hidden"}`}
      >
        <Select
          name="city"
          placeholder="Cidade"
          // disabled={isLoading}
          value={city}
          onChange={(e) => {
            setCity(e.target.value);
            setCityName(e.target.value);
            handleBlurUserJourney("cityName", e.target.value);
          }}
        >
          {cities?.map((item) => (
            <SelectItem key={item.id} value={item.nome}>
              {item.nome}
            </SelectItem>
          ))}
        </Select>
      </Column>

      <Column
        size={12}
        className={showStreetAndNeighborhoodInputs() ? "pb-6" : "hidden"}
      >
        <Input
          name="address.street"
          label="Rua"
          placeholder="Rua"
          value={street}
          disabled={!!saleId}
          onChange={(e) => setStreet(e.target.value)}
          onBlur={(e) => {
            setStreet(e.target.value);
            handleBlurUserJourney("street", e.target.value);
          }}
        />
      </Column>

      <Column
        size={12}
        className={showStreetAndNeighborhoodInputs() ? "pb-6" : "hidden"}
      >
        <Input
          name="address.neighborhood"
          label="Bairro"
          placeholder="Bairro"
          value={neighborhood}
          disabled={!!saleId}
          onChange={(e) => setNeighborhood(e.target.value)}
          onBlur={(e) => {
            setNeighborhood(e.target.value);
            handleBlurUserJourney("neighborhood", e.target.value);
          }}
        />
      </Column>
      {showCityAndStateInputs() && (
        <>
          <Column size={12} className="mb-8">
            <Button
              type="button"
              onClick={handleFindAddress}
              large
              // disabled={!formIsValid}
              // helperText={!formIsValid ? 'todos os campos são obrigatórios' : ''}
              loading={loading}
            >
              Buscar CEP
            </Button>
          </Column>
          <Columns
            size={12}
            className="max-h-80 w-full overflow-auto pb-4"
            justifyContent="center"
            alignItems="center"
          >
            {addresses && !addresses.length ? (
              <Column size={12} className="max-h-80 w-full overflow-auto">
                <Text
                  as="h1"
                  kind="blue"
                  size="base"
                  className="m-auto w-full pt-2 text-center font-bold"
                >
                  Endereço não encontrado
                </Text>
              </Column>
            ) : (
              addresses &&
              addresses.map((address) => (
                <>
                  <Column
                    size={12}
                    className="max-h-80 w-full overflow-auto px-2 pb-4"
                  >
                    <Text
                      as="h1"
                      kind="blue"
                      size="base"
                      className="pt-2 font-bold"
                    >
                      Escolha um endereço
                    </Text>
                  </Column>

                  <Column size={12} className="w-full pb-4" key={address.index}>
                    <AddressCard
                      cep={address.cep}
                      street={address.rua}
                      neighborhood={address.bairro}
                      state={address.uf}
                      showIconAdd
                      onClick={() => {
                        setStreet((old) => (address.rua ? address.rua : old));
                        setZipCode(formatZipCode(address.cep));
                        setNeighborhood((old) =>
                          address.bairro ? address.bairro : old
                        );

                        handleBlurUserJourney("zipCode", address.cep);
                        if (address.rua) {
                          handleBlurUserJourney("street", address.rua);
                        }
                        if (address.bairro) {
                          handleBlurUserJourney("neighborhood", address.bairro);
                        }

                        setAddresses([]);
                      }}
                    />
                  </Column>
                </>
              ))
            )}
          </Columns>
        </>
      )}

      <div className="relative w-full">
        <Column
          size={12}
          className={
            (zipCode && !!data && errorMessage === false) ||
            form.address.neighborhood
              ? "pb-6"
              : "hidden"
          }
        >
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm text-tim-primary">Número</label>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-xs text-gray-500">Sem Número</span>
              <div
                className={`relative w-9 h-5 rounded-full transition-colors ${
                  noNumber ? "bg-tim-primary" : "bg-gray-300"
                }`}
                onClick={() => {
                  if (saleId) return;
                  const newValue = !noNumber;
                  setNoNumber(newValue);
                  setValue("address.noNumber", newValue);
                  if (newValue) {
                    setNumber("0");
                    setValue("address.number", "0");
                  } else {
                    setNumber("");
                    setValue("address.number", "");
                  }
                }}
              >
                <div
                  className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    noNumber ? "translate-x-4" : "translate-x-0.5"
                  }`}
                />
              </div>
            </label>
          </div>
          {!noNumber && (
            <Input
              name="address.number"
              placeholder="Ex: 123, Qd 3 Lt 15"
              value={number || ""}
              disabled={!!saleId}
              onChange={(e) => {
                setNumber(e.target.value);
              }}
              onBlur={(e) => {
                setNumber(e.target.value);
                handleBlurUserJourney("number", e.target.value);
              }}
            />
          )}
        </Column>

        <Column
          size={12}
          className={
            (zipCode && !!data && errorMessage === false) ||
            form.address.neighborhood
              ? "pb-6"
              : "hidden"
          }
        >
          <Input
            name="address.complement"
            label="Complemento"
            placeholder="Complemento"
            value={complement || ""}
            disabled={!!saleId}
            onChange={(e) => setComplement(e.target.value)}
            onBlur={(e) => {
              setComplement(e.target.value);
              handleBlurUserJourney("complement", e.target.value);
            }}
          />
        </Column>

        <Column
          size={12}
          className={
            (zipCode && !!data && errorMessage === false) ||
            form.address.neighborhood
              ? "pb-6"
              : "hidden"
          }
        >
          <Input
            name="address.referencePoint"
            label="Ponto de Referência"
            placeholder="Ex: Em frente ao mercado"
            value={referencePoint || ""}
            disabled={!!saleId}
            onChange={(e) => setReferencePoint(e.target.value)}
            onBlur={(e) => {
              setReferencePoint(e.target.value);
              handleBlurUserJourney("referencePoint", e.target.value);
            }}
          />
        </Column>

        <Column size={12}>{isLoading && <LoadingAddress />}</Column>
      </div>
    </>
  );
};

export default AddressInfoFields;
